import React from 'react';
import { matchPath } from 'react-router-dom';

import Loader from '../components/common/Loader';
import PagedListSkeleton from '../components/common/PagedListSkeleton';
import PanelSkeleton from '../components/common/PanelSkeleton';
import PanelStudentSkeleton from '../components/common/PanelStudentSkeleton';
import FormGridSkeleton from '../components/common/FormGridSkeleton';
import PageDetailSkeleton from '../components/common/PageDetailSkeleton';

import { PrivateRoutesConfig } from '../config/PrivateRoutesConfig';

/**
 * Suspense fallback map — REDLINE-loading.md §5. Cada rota do
 * `PrivateRoutesConfig` declara `fallback: 'list'|'panel'|'form'|...`
 * explicitamente. Rota sem declaração → BrandLoader neutro (nunca mente).
 *
 * <p>Compartilhado entre App.js (per-route Suspense) e MainPage.js (Suspense
 * do Outlet — precisa saber a rota corrente pra escolher o skeleton certo).
 * Sem essa unificação, o MainPage Suspense hardcoded em `<Loader/>` aparecia
 * quando o per-route boundary ainda não estava estabelecido pelo React (timing
 * de concurrent rendering + chunks lazy grandes).
 */
export const FALLBACK_MAP = {
	list: <PagedListSkeleton />,
	panel: <PanelSkeleton />,
	'panel-student': <PanelStudentSkeleton />,
	form: <FormGridSkeleton />,
	'school-class-detail': <PageDetailSkeleton />,
	// Studio (Quiz/Exam): 2 tabs (Geral · Questões).
	'quiz-exam-detail': <PageDetailSkeleton tabWidths={[80, 90]} />,
	// Studio (Questão): 4 tabs (Pergunta · Resposta · Resolução · Classificação).
	'question-detail': <PageDetailSkeleton tabWidths={[80, 80, 90, 110]} />,
	// Admin (Membro): seções empilhadas, SEM tabs (REDLINE-membros §8 matou as
	// 3 que existiam). `tabWidths={[]}` omite a barra — e o `isLoading` da
	// página passa exatamente o mesmo, senão o conteúdo pula entre os dois.
	'school-member-detail': <PageDetailSkeleton tabWidths={[]} />,
	// Studio (Import PDF): página linear (sem tabs) — skeleton sem tab-bar.
	'question-import': <PageDetailSkeleton tabWidths={[]} />,
	// Studio (Gerar por IA): página linear irmã do import — mesmo skeleton.
	'question-generate': <PageDetailSkeleton tabWidths={[]} />,
	// ⚠️ Os dois `*-update` do Console usam PageDetailSkeleton, não `form`: o
	// `FormGridSkeleton` não tem BARRA DE TABS, e as duas páginas são card com
	// tabs + rodapé — mesma anatomia do detalhe. Skeleton sem a barra faz o
	// conteúdo pular pra baixo quando o form real entra.
	// Larguras batem os rótulos reais: Geral · Usuários · Marca.
	'school-update': <PageDetailSkeleton tabWidths={[70, 90, 70]} />,
	// Geral · Escolas · Controle de Acesso · Integração.
	'user-update': <PageDetailSkeleton tabWidths={[70, 80, 140, 110]} />,
};

/** Retorna o skeleton do `fallback` declarado, ou `<Loader/>` se null/desconhecido. */
export function resolveFallback(fallback) {
	return FALLBACK_MAP[fallback] || <Loader />;
}

/**
 * Resolve o fallback certo pelo pathname corrente — usado pelo MainPage
 * Suspense. Faz match no `PrivateRoutesConfig` na ordem (primeiro que bate
 * ganha, exact routes primeiro). Se nada bate ou a rota não declara fallback,
 * cai no BrandLoader.
 */
export function resolveFallbackFromPath(pathname) {
	if (!pathname) return <Loader />;
	const route = PrivateRoutesConfig.find(r =>
		matchPath({ path: r.path, end: r.exact }, pathname)
	);
	return resolveFallback(route?.fallback);
}
